// ✅ ALTERED ONLY FOR THEME (matches your “above theme”)
// - Orange header bar + white text
// - Page bg #F2F2F2 + inner white surfaces with soft blue border
// - Left orange accent stripe for Filters/Actions section
// - Inputs/selects: soft blue border + orange focus
// - Buttons: primary orange, outlined blue, danger stays red but styled
// - Tables: blue header, soft zebra rows, hover tint, subtle borders
// - Checkbox: blue default, orange when checked
// - No API/business logic changed

import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  Checkbox,
  InputLabel,
  FormControl,
  Grid,
  Divider,
} from "@mui/material";

import { API_URL, API_UPLOAD_URL } from "./config";

const TransportManagement = () => {
  // ✅ Theme constants
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
    "& .MuiInputLabel-root.Mui-focused": { color: THEME_ORANGE },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const dangerBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
  };

  const tableWrapSx = {
    ...surfaceSx,
    mt: 2,
    overflow: "hidden",
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
      py: 1,
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)", py: 0.8 },
  });

  const checkboxSx = {
    color: "rgba(25,118,210,0.75)",
    "&.Mui-checked": { color: THEME_ORANGE },
  };

  // ===== Original state/logic =====
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [logincampusCode, setCampusCode] = useState("");
  const [busNumbers, setBusNumbers] = useState([]);
  const [bulkReassignBus, setBulkReassignBus] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterGender, setFilterGender] = useState("");
  const [filterBus, setFilterBus] = useState("");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const token = localStorage.getItem("auth");

  // Fetch CampusCode from JWT
  useEffect(() => {
    if (token) {
      try {
        const decodedToken = JSON.parse(atob(token.split(".")[1]));
        setCampusCode(decodedToken.campusCode);
      } catch (err) {
        console.error("Error decoding token:", err);
        setError("Failed to decode authentication token.");
      }
    }
  }, []);

  useEffect(() => {
    if (!logincampusCode) return;
    fetchStudents();
    fetchBusNumbers();
  }, [logincampusCode]);

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/getTransportStudents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStudents(response.data);
      setFilteredStudents(response.data);
    } catch (err) {
      setError("Failed to fetch students");
    }
  };

  const fetchBusNumbers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/getBusNumbers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBusNumbers(response.data);
    } catch (err) {
      setError("Failed to fetch bus numbers");
    }
  };

  useEffect(() => {
    let filtered = students;
    if (filterStatus) filtered = filtered.filter((s) => s.IsStudentActive.toString() === filterStatus);
    if (filterGender) filtered = filtered.filter((s) => s.Gender === filterGender);
    if (filterBus) filtered = filtered.filter((s) => s.BusNumber === filterBus);
    setFilteredStudents(filtered);
  }, [filterStatus, filterGender, filterBus, students]);

  const handleSelectStudent = (id) => {
    setSelectedStudentIds((prev) => (prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]));
  };

  const handleSelectAll = () => {
    setSelectedStudentIds(
      selectedStudentIds.length === filteredStudents.length ? [] : filteredStudents.map((s) => s.id)
    );
  };

  const reassignSelectedStudents = async () => {
    if (!bulkReassignBus) {
      setError("Please select a bus number");
      return;
    }
    try {
      await axios.post(
        `${API_URL}/api/bulkReassignBus`,
        {
          studenttransportIds: selectedStudentIds,
          newBusId: bulkReassignBus,
          StopID: students.StopID,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(true);
      fetchStudents();
      setSelectedStudentIds([]);
    } catch (err) {
      setError("Failed to reassign students");
    }
  };

  const removeSelectedStudents = async () => {
    try {
      await axios.post(
        `${API_URL}/api/removeStudentsFromTransport`,
        { studenttransportIds: selectedStudentIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(true);
      fetchStudents();
      setSelectedStudentIds([]);
    } catch (err) {
      setError("Failed to remove students");
    }
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Orange header bar */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Transport Management
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Filter students and manage transport assignment
            </Typography>
          </Box>

          {/* (optional slot for future actions) */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {/* Example: keep empty for now */}
          </Box>
        </Box>

        <Box sx={bodySx}>
          {/* Messages */}
          {error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{
                borderRadius: 2,
                border: "1px solid rgba(244,67,54,0.25)",
                backgroundColor: "rgba(244,67,54,0.04)",
                mb: 2,
              }}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Snackbar open autoHideDuration={3000} onClose={() => setSuccess(false)}>
              <Alert severity="success" sx={{ borderRadius: 2 }} onClose={() => setSuccess(false)}>
                Success!
              </Alert>
            </Snackbar>
          )}

          {/* ✅ Filters + Actions section */}
          <Paper variant="outlined" sx={sectionSx}>
            <Box sx={{ pl: 1 }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                Filters
              </Typography>
              <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

              <Grid container spacing={1.2} alignItems="center">
                {/* Student Status */}
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="status-filter-label">Student Status</InputLabel>
                    <Select
                      labelId="status-filter-label"
                      value={filterStatus}
                      label="Student Status"
                      onChange={(e) => setFilterStatus(e.target.value)}
                      sx={inputSx}
                    >
                      <MenuItem value="">All Students</MenuItem>
                      <MenuItem value="1">Active Students</MenuItem>
                      <MenuItem value="0">Inactive Students</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Gender */}
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="gender-filter-label">Gender</InputLabel>
                    <Select
                      labelId="gender-filter-label"
                      value={filterGender}
                      label="Gender"
                      onChange={(e) => setFilterGender(e.target.value)}
                      sx={inputSx}
                    >
                      <MenuItem value="">All Genders</MenuItem>
                      <MenuItem value="M">Male</MenuItem>
                      <MenuItem value="F">Female</MenuItem>
                      <MenuItem value="O">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Bus */}
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="bus-filter-label">Bus Number</InputLabel>
                    <Select
                      labelId="bus-filter-label"
                      value={filterBus}
                      label="Bus Number"
                      onChange={(e) => setFilterBus(e.target.value)}
                      sx={inputSx}
                    >
                      <MenuItem value="">All</MenuItem>
                      {busNumbers.map((bus) => (
                        <MenuItem key={bus.BusID} value={bus.BusNumber}>
                          {bus.BusNumber}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Actions row */}
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end", mt: 0.5 }}>
                    {/* Optional reassign UI (kept disabled in your code; styling ready) */}
                    {/* 
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                      <InputLabel id="bulk-bus-label">Reassign Bus</InputLabel>
                      <Select
                        labelId="bulk-bus-label"
                        value={bulkReassignBus}
                        label="Reassign Bus"
                        onChange={(e) => setBulkReassignBus(e.target.value)}
                        sx={inputSx}
                      >
                        <MenuItem value="">Select Bus</MenuItem>
                        {busNumbers.map((bus) => (
                          <MenuItem key={bus.BusID} value={bus.BusID}>
                            {bus.BusNumber} (Cap: {bus.Capacity}) (Occ: {bus.Occupied})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Button
                      variant="contained"
                      sx={{ ...primaryBtnSx, minWidth: 180 }}
                      onClick={reassignSelectedStudents}
                      disabled={!selectedStudentIds.length}
                    >
                      Reassign Selected
                    </Button>
                    */}

                    <Button
                      color="error"
                      variant="contained"
                      sx={{ ...dangerBtnSx, minWidth: 200 }}
                      onClick={removeSelectedStudents}
                      disabled={!selectedStudentIds.length}
                    >
                      Remove Selected
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>

          {/* ✅ Table */}
          {/* OUTER – keeps rounded corners & spacing */}
        <Paper elevation={0} sx={tableWrapSx}>
          
          {/* INNER – ONLY for horizontal scroll */}
          <Box
            sx={{
              overflowX: "auto",
              overflowY: "hidden",
            }}
          >
            <TableContainer component="div">
            <Table size="small">
              <TableHead>
                <TableRow sx={theadRowSx}>
                  <TableCell sx={{ width: 60 }}>#</TableCell>
                  <TableCell>Sub Campus</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Gender</TableCell>
                  <TableCell>Semester</TableCell>
                  <TableCell>Bus Number</TableCell>
                  <TableCell>Driver Name</TableCell>
                  <TableCell>Capacity</TableCell>
                  <TableCell>Occupied</TableCell>
                  <TableCell>Stop Name</TableCell>
                  <TableCell>Is Student Active</TableCell>
                  <TableCell sx={{ width: 70 }}>
                    <Checkbox
                      sx={checkboxSx}
                      checked={filteredStudents.length > 0 && selectedStudentIds.length === filteredStudents.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredStudents.map((student, index) => (
                  <TableRow key={student.id} sx={zebraRowSx(index)}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{student.SubCampusCode}</TableCell>
                    <TableCell>{student.FullName}</TableCell>
                    <TableCell>{student.Gender}</TableCell>
                    <TableCell>{student.Semester}</TableCell>
                    <TableCell>{student.BusNumber}</TableCell>
                    <TableCell>{student.DriverName}</TableCell>
                    <TableCell>{student.Capacity}</TableCell>
                    <TableCell>{student.Occupied}</TableCell>
                    <TableCell>{student.StopName}</TableCell>
                    <TableCell>{student.IsStudentActive}</TableCell>
                    <TableCell>
                      <Checkbox
                        sx={checkboxSx}
                        checked={selectedStudentIds.includes(student.id)}
                        onChange={() => handleSelectStudent(student.id)}
                      />
                    </TableCell>
                  </TableRow>
                ))}

                {filteredStudents.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={12} sx={{ py: 3, textAlign: "center", color: "rgba(25,118,210,0.75)" }}>
                      No students found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>
          </Paper>
        </Box>
      </Paper>
    </Box>
  );
};

export default TransportManagement;
